import React, { useState, useEffect, useRef } from "react";
import Section from "./layouts/Section";
import { SiteWide } from "./customComponents";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { userSelect, homeScreenStatSelect } from '../redux/selectors/index';
import { money } from '../utility/Helper1';
import { chattyActions } from "../redux/actions";

// Material-UI Icons
import VerifiedIcon from "@mui/icons-material/Verified";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AddIcon from "@mui/icons-material/Add";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import SearchIcon from "@mui/icons-material/Search";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PushPinIcon from "@mui/icons-material/PushPin";
import ImageIcon from "@mui/icons-material/Image";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CallIcon from "@mui/icons-material/Call";
import VideocamIcon from "@mui/icons-material/Videocam";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DoneIcon from "@mui/icons-material/Done";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import AccountBalanceWalletIcon from "@mui/icons-material/Atm";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TimerIcon from "@mui/icons-material/Timer";

const TABS = ["All", "Active", "Completed"];

const EMOJIS = [
    "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇", "🙂", "😉", "😌", "😍", "🥰", "😘",
    "😗", "😙", "😚", "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", "😎", "🤩", "🥳", "😏", "😒",
    "😞", "😔", "😟", "😕", "🙁", "☹️", "😣", "😖", "😫", "😩", "🥺", "😢", "😭", "😤", "😠", "😡",
    "🤬", "🤯", "😳", "🥵", "🥶", "😱", "😨", "😰", "😥", "😓", "🤔", "🤭", "🤫", "🤥", "😶", "😐",
    "😑", "😬", "🙄", "😯", "😦", "😧", "😮", "😲", "🥱", "😴", "🤤", "😪", "😵", "🤐", "🥴", "🤢",
    "🤮", "🤧", "😷", "🤒", "🤕", "🤑", "🤠", "😈", "👿", "👹", "👺", "🤡", "💩", "👻", "💀", "☠️",
    "👽", "👾", "🤖", "🎃", "👋", "🤚", "🖐️", "✋", "🖖", "👌", "🤌", "🤏", "✌️", "🤞", "🤟", "🤘",
    "🤙", "👈", "👉", "👆", "🖕", "👇", "☝️", "👍", "👎", "✊", "👊", "🤛", "🤜", "👏", "🙌", "👐",
    "🤲", "🤝", "🙏", "✍️", "💅", "🤳", "💪", "🦾", "🦿", "🦵", "🦶", "👂", "🦻", "👃", "🧠", "👀",
    "👅", "👄", "💋", "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❤️‍🔥", "❤️‍🩹", "❣️",
    "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💟", "✨", "🌟", "⭐", "💫", "💥", "🔥", "🌈", "☀️"
];

const splitIntoSentences = (text) => {
    if (!text) return [];
    const rawChunks = text.split(/(\.)(?=\s|$)/);
    const chunks = [];
    for (let i = 0; i < rawChunks.length; i++) {
        let chunk = rawChunks[i];
        if (!chunk) continue;
        if (chunk === ".") {
            if (chunks.length > 0) {
                chunks[chunks.length - 1] += ".";
            }
        } else {
            chunks.push(chunk.trim());
        }
    }
    return chunks.filter(Boolean);
};

const preprocessMessages = (rawMessages) => {
    const processed = [];
    rawMessages.forEach(msg => {
        if (msg.role === "assistant" && !msg.isTemp) {
            const chunks = splitIntoSentences(msg.message);
            if (chunks.length > 0) {
                chunks.forEach((chunkText, idx) => {
                    processed.push({
                        ...msg,
                        id: `${msg.id}-chunk-${idx}`,
                        message: chunkText
                    });
                });
            } else {
                processed.push(msg);
            }
        } else {
            processed.push(msg);
        }
    });
    return processed;
};

function Chats() {
    const userData = useSelector(userSelect);
    const finance = useSelector(homeScreenStatSelect);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [chats, setChats] = useState([]);
    const [topics, setTopics] = useState([]);
    const [activeChatId, setActiveChatId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState("All");
    const [isTyping, setIsTyping] = useState(false);
    const [loadingChats, setLoadingChats] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [showInfoDrawer, setShowInfoDrawer] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const endRef = useRef(null);
    const timeoutsRef = useRef([]);
    const inputRef = useRef(null);

    const clearPendingTimeouts = () => {
        timeoutsRef.current.forEach(t => clearTimeout(t));
        timeoutsRef.current = [];
    };

    // ────────────────────────────────────────────────────────────────────────
    // Fetch Chats list on Mount
    // ────────────────────────────────────────────────────────────────────────
    const fetchChats = (showLoading = true) => {
        if (showLoading) setLoadingChats(true);
        dispatch(chattyActions.chattyLoad((res) => {
            setLoadingChats(false);
            if (res && res.code === 1) {
                setChats(res.data.chats || []);
                setTopics(res.data.topics || []);
            }
        }));
    };

    useEffect(() => {
        fetchChats();
    }, []);

    const displayChunksSequentially = (chunks, baseMsgs, finalMsgs, aiMsgId) => {
        clearPendingTimeouts();

        const preprocessedBase = preprocessMessages(baseMsgs);
        setMessages(preprocessedBase);
        setIsTyping(true);

        let currentMessages = [...preprocessedBase];

        chunks.forEach((chunkText, index) => {
            const delay = (index + 1) * 3000; // 3s, 6s, 9s...

            const timeoutId = setTimeout(() => {
                const chunkMsg = {
                    id: `${aiMsgId}-chunk-${index}`,
                    chattyId: activeChatId,
                    role: "assistant",
                    message: chunkText,
                    timeCreated: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                };

                currentMessages = [...currentMessages, chunkMsg];

                if (index === chunks.length - 1) {
                    setMessages(preprocessMessages(finalMsgs));
                    setIsTyping(false);
                } else {
                    setMessages(currentMessages);
                    setIsTyping(true);
                }
            }, delay);

            timeoutsRef.current.push(timeoutId);
        });
    };

    // ────────────────────────────────────────────────────────────────────────
    // Fetch Messages when activeChatId changes
    // ────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        setShowEmojiPicker(false);
        clearPendingTimeouts();
        if (activeChatId) {
            setMessages([]);
            const currentChatId = activeChatId;
            setLoadingMessages(true);
            dispatch(chattyActions.chattyGetMessages(activeChatId, (res) => {
                if (activeChatId !== currentChatId) return;
                setLoadingMessages(false);
                if (res && res.code === 1) {
                    setMessages(preprocessMessages(res.data.messages || []));
                }
            }));

            // Mark chat unread as 0 locally
            setChats(prev => prev.map(c => c.id === activeChatId ? { ...c, unread: 0 } : c));
        } else {
            setLoadingMessages(false);
        }
        return () => clearPendingTimeouts();
    }, [activeChatId]);

    // ────────────────────────────────────────────────────────────────────────
    // Scroll to end of messages
    // ────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (endRef.current) {
            endRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, activeChatId, isTyping]);

    // ────────────────────────────────────────────────────────────────────────
    // Send Message
    // ────────────────────────────────────────────────────────────────────────
    const send = (msgText = null) => {
        const textToSend = msgText || input.trim();
        if (!textToSend || !activeChatId) return;

        setShowEmojiPicker(false);

        // Construct temporary user message
        const tempMsgId = Date.now();
        const userMsg = {
            id: tempMsgId,
            chattyId: activeChatId,
            role: "user",
            message: textToSend,
            timeCreated: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            isTemp: true
        };

        setMessages(prev => [...prev, userMsg]);
        setInput("");

        dispatch(chattyActions.chattySendMessage(activeChatId, textToSend, (res) => {
            if (res && res.code === 1) {
                // Refresh chats to update previews
                fetchChats(false);

                const allMsgs = res.data.messages || [];
                let baseMsgs = [...allMsgs];
                let lastAiMsg = null;

                if (allMsgs.length > 0 && allMsgs[allMsgs.length - 1].role === "assistant") {
                    lastAiMsg = allMsgs[allMsgs.length - 1];
                    baseMsgs = allMsgs.slice(0, -1);
                }

                if (lastAiMsg) {
                    const chunks = splitIntoSentences(lastAiMsg.message);
                    if (chunks.length > 0) {
                        displayChunksSequentially(chunks, baseMsgs, allMsgs, lastAiMsg.id);
                    } else {
                        setMessages(preprocessMessages(allMsgs));
                        setIsTyping(false);
                    }
                } else {
                    setMessages(preprocessMessages(allMsgs));
                    setIsTyping(false);
                }
            } else {
                setIsTyping(false);
                // Remove temp message or mark error
                setMessages(prev => prev.filter(m => m.id !== tempMsgId));
                alert(res?.msg || "Failed to send message. Please try again.");
            }
        }));
    };

    // ────────────────────────────────────────────────────────────────────────
    // Filters & Tabs
    // ────────────────────────────────────────────────────────────────────────
    const getFiltered = () => {
        let list = chats;
        if (activeTab === "Active") list = list.filter(c => Number(c.active) === 1);
        if (activeTab === "Completed") list = list.filter(c => Number(c.active) === 0);
        if (search) list = list.filter(c => c.chatName.toLowerCase().includes(search.toLowerCase()));
        return list;
    };

    const filtered = getFiltered();
    const totalUnread = chats.reduce((sum, c) => sum + (c.unread || 0), 0);
    const activeChat = chats.find(c => c.id === activeChatId);

    // ── Active Now / Stories Strip ────────────────────────────────────────────
    const renderActiveStrip = () => {
        const activeOnly = chats.filter(c => Number(c.active) === 1);
        return (
            <div style={{ overflowX: "auto", scrollbarWidth: "none" }}>
                <div style={{ display: "flex", gap: 12, padding: "4px 2px 6px", width: "max-content" }}>
                    {/* My story */}
                    {activeOnly.map(c => (
                        <div key={c.id} onClick={() => setActiveChatId(c.id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0, cursor: "pointer" }}>
                            <div style={{ position: "relative" }}>
                                <div style={{
                                    width: 50, height: 50, borderRadius: "50%",
                                    padding: 2, background: `linear-gradient(135deg,${c.chatColor},${c.chatColor}88)`,
                                    boxShadow: `0 2px 10px ${c.chatColor}33`
                                }}>
                                    <div style={{
                                        width: "100%", height: "100%", borderRadius: "50%",
                                        background: `linear-gradient(135deg,${c.chatColor},${c.chatColor}aa)`,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "'Syne',sans-serif"
                                    }}>
                                        {c.chatInitials}
                                    </div>
                                </div>
                                <div style={{
                                    position: "absolute", bottom: 2, right: 2, width: 10, height: 10,
                                    borderRadius: "50%", background: "#22c55e", border: "2px solid var(--body-bg, #f8fafc)"
                                }} />
                            </div>
                            <span style={{ color: "var(--text-muted, #64748b)", fontSize: 9, fontFamily: "'DM Sans',sans-serif", maxWidth: 50, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.chatName}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // ── Empty State ───────────────────────────────────────────────────────────
    const renderEmptyState = () => (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", textAlign: "center" }}>
            <div style={{
                width: 80, height: 80, borderRadius: "50%", marginBottom: 16,
                background: "linear-gradient(135deg,rgba(59,130,246,0.1),rgba(59,130,246,0.05))",
                border: "1px solid rgba(59,130,246,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center"
            }}>
                <ChatBubbleOutlineIcon style={{ fontSize: 36, color: "#3b82f6", opacity: 0.6 }} />
            </div>
            <p style={{ color: "var(--text-main, #0f172a)", fontSize: 16, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0, marginBottom: 8 }}>
                {search ? "No matches found" : "No Chat"}
            </p>
            <p style={{ color: "var(--text-muted, #94a3b8)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: "0 0 20px 0", lineHeight: 1.6, maxWidth: 280 }}>
                {search
                    ? `No conversations match "${search}"`
                    : "Create a chat session! Chat daily to grow your earnings."}
            </p>
            {!search && (
                <button
                    onClick={() => navigate('/deposit')}
                    style={{
                        padding: "10px 24px", borderRadius: 24, border: "none", cursor: "pointer",
                        background: "linear-gradient(135deg,#3b82f6,#2563eb)", color: "#fff",
                        fontSize: 13, fontWeight: 600, fontFamily: "'Syne',sans-serif",
                        boxShadow: "0 4px 14px rgba(59,130,246,0.3)"
                    }}
                >
                    Start A Session
                </button>
            )}
        </div>
    );

    // ── Chat List Item ────────────────────────────────────────────────────────
    const renderContactCard = (c) => {
        const lastMsgIcon = c.lastMsgRole === "assistant"
            ? null
            : c.lastMsgRole === "user"
                ? <DoneAllIcon style={{ fontSize: 12, color: "#3b82f6", marginRight: 3 }} />
                : null;

        const isSessionActive = Number(c.active) === 1 && !c.isExpired;

        return (
            <button
                key={c.id}
                onClick={() => setActiveChatId(c.id)}
                style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "12px 0",
                    background: "transparent", border: "none", cursor: "pointer", outline: "none",
                    width: "100%", textAlign: "left", textDecoration: "none", boxShadow: "none",
                    borderBottom: "1px solid var(--border-color, rgba(59,130,246,0.05))"
                }}
            >
                {/* Avatar */}
                <div style={{ position: "relative", flexShrink: 0 }}>
                    <div style={{
                        width: 52, height: 52, borderRadius: "50%",
                        background: `linear-gradient(135deg,${c.chatColor},${c.chatColor}88)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontSize: 16, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                        boxShadow: `0 4px 12px ${c.chatColor}30`,
                        position: "relative"
                    }}>
                        {c.chatInitials}
                    </div>
                    <div style={{
                        position: "absolute", bottom: 2, right: 2, width: 12, height: 12,
                        borderRadius: "50%", background: isSessionActive ? "#22c55e" : "#94a3b8", border: "2px solid var(--body-bg, #f8fafc)"
                    }} />
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{ color: "var(--text-main, #0f172a)", fontSize: 14, fontWeight: c.unread > 0 ? 700 : 600, fontFamily: "'Syne',sans-serif", textDecoration: "none" }}>
                                {c.chatName}
                            </span>
                            {isSessionActive && <VerifiedIcon style={{ fontSize: 12, color: "#3b82f6" }} />}
                            {!isSessionActive && <span style={{ fontSize: 9, padding: "1px 6px", background: "var(--border-color, #e2e8f0)", color: "var(--text-muted, #64748b)", borderRadius: 8, fontWeight: 700 }}>COMPLETED</span>}
                        </div>
                        <span style={{ color: c.unread > 0 ? "#2563eb" : "var(--text-muted, #94a3b8)", fontSize: 10, fontFamily: "'DM Sans',sans-serif", fontWeight: c.unread > 0 ? 600 : 400 }}>
                            {c.daysEarned}d / {c.tenure}d
                        </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", overflow: "hidden", flex: 1 }}>
                            {lastMsgIcon}
                            <p style={{
                                color: c.unread > 0 ? "var(--text-main, #334155)" : "var(--text-muted, #94a3b8)",
                                fontSize: 12, fontFamily: "'DM Sans',sans-serif",
                                margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                fontWeight: c.unread > 0 ? 500 : 400,
                                maxWidth: "85%"
                            }}>
                                {c.lastMsg || `Say hello to ${c.chatName}! 👋`}
                            </p>
                        </div>
                        {c.unread > 0 && (
                            <div style={{
                                width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                                background: "linear-gradient(135deg,#3b82f6,#2563eb)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: "'Syne',sans-serif"
                            }}>{c.unread}</div>
                        )}
                    </div>
                    {/* Small progress line */}
                    <div style={{ width: "100%", height: 3, background: "var(--border-color, rgba(255,255,255,0.08))", borderRadius: 2, marginTop: 6, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${c.progress || 0}%`, background: isSessionActive ? "linear-gradient(90deg, #3b82f6, #10b981)" : "#94a3b8" }} />
                    </div>
                </div>
            </button>
        );
    };

    // ── Chat Info Drawer / Sidebar ─────────────────────────────────────────────
    const renderInfoDrawer = () => {
        if (!activeChat) return null;
        return (
            <div style={{
                position: "absolute", top: 58, right: 0, bottom: 0, width: "100%", maxWidth: 300,
                background: "var(--card-bg, #ffffff)", borderLeft: "1px solid var(--border-color, rgba(59,130,246,0.08))",
                boxShadow: "var(--card-shadow, -4px 0 24px rgba(0,0,0,0.06))", display: "flex", flexDirection: "column",
                zIndex: 10, padding: 20, overflowY: "auto"
            }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <h3 style={{ color: "var(--text-main, #0f172a)", fontSize: 16, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>Session Parameters</h3>
                    <button onClick={() => setShowInfoDrawer(false)} style={{ border: "none", background: "none", color: "#3b82f6", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Close</button>
                </div>

                {/* Avatar big */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 24 }}>
                    <div style={{
                        width: 72, height: 72, borderRadius: "50%",
                        background: `linear-gradient(135deg,${activeChat.chatColor},${activeChat.chatColor}88)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontSize: 24, fontWeight: 800, fontFamily: "'Syne',sans-serif",
                        boxShadow: `0 8px 24px ${activeChat.chatColor}30`, marginBottom: 8
                    }}>
                        {activeChat.chatInitials}
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "var(--text-main, #0f172a)", fontFamily: "'Syne',sans-serif" }}>{activeChat.chatName}</span>
                    <span style={{ fontSize: 11, color: Number(activeChat.active) === 1 ? "#22c55e" : "var(--text-muted, #94a3b8)", fontWeight: 600, marginTop: 4 }}>
                        {Number(activeChat.active) === 1 ? "● Running Session" : "● Completed"}
                    </span>
                </div>

                {/* Financial details */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div style={{ background: "var(--body-bg, #f8fafc)", padding: 12, borderRadius: 12, border: "1px solid var(--border-color, rgba(59,130,246,0.05))" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <AccountBalanceWalletIcon style={{ fontSize: 16, color: "#3b82f6" }} />
                            <span style={{ color: "var(--text-muted, #64748b)", fontSize: 11, fontWeight: 600 }}>Capital Investment</span>
                        </div>
                        <span style={{ color: "var(--text-main, #0f172a)", fontSize: 15, fontWeight: 700 }}>{activeChat.capital} {activeChat.currencyAbbr}</span>
                    </div>

                    <div style={{ background: "var(--body-bg, #f8fafc)", padding: 12, borderRadius: 12, border: "1px solid var(--border-color, rgba(59,130,246,0.05))" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <TrendingUpIcon style={{ fontSize: 16, color: "#10b981" }} />
                            <span style={{ color: "var(--text-muted, #64748b)", fontSize: 11, fontWeight: 600 }}>Accumulated Profit</span>
                        </div>
                        <span style={{ color: "#10b981", fontSize: 15, fontWeight: 700 }}>+{activeChat.profit} {activeChat.currencyAbbr}</span>
                    </div>

                    <div style={{ background: "var(--body-bg, #f8fafc)", padding: 12, borderRadius: 12, border: "1px solid var(--border-color, rgba(59,130,246,0.05))" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <TimerIcon style={{ fontSize: 16, color: "#f59e0b" }} />
                            <span style={{ color: "var(--text-muted, #64748b)", fontSize: 11, fontWeight: 600 }}>Daily ROI Rate</span>
                        </div>
                        <span style={{ color: "#f59e0b", fontSize: 15, fontWeight: 700 }}>{activeChat.dailyRoi}% daily</span>
                    </div>

                    <div style={{ background: "var(--body-bg, #f8fafc)", padding: 12, borderRadius: 12, border: "1px solid var(--border-color, rgba(59,130,246,0.05))" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <CalendarTodayIcon style={{ fontSize: 16, color: "#6366f1" }} />
                            <span style={{ color: "var(--text-muted, #64748b)", fontSize: 11, fontWeight: 600 }}>Tenure Period</span>
                        </div>
                        <span style={{ color: "var(--text-main, #6366f1)", fontSize: 15, fontWeight: 700 }}>{activeChat.daysEarned} / {activeChat.tenure} Chat Days</span>
                        <div style={{ width: "100%", height: 4, background: "var(--border-color, #e2e8f0)", borderRadius: 2, marginTop: 8, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${activeChat.progress || 0}%`, background: "linear-gradient(90deg, #6366f1, #a855f7)" }} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ── Chat Window ───────────────────────────────────────────────────────────
    const renderChatWindow = () => {
        if (!activeChat) return null;

        const isSessionActive = Number(activeChat.active) === 1 && !activeChat.isExpired;

        return (
            <div className="chats-height-container" style={{ display: "flex", flexDirection: "column", flex: 1, background: "var(--body-bg, #f8fafc)", position: "relative" }}>

                {/* Chat Header */}
                <div style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                    background: "var(--bar-bg, rgba(255,255,255,0.97))", borderBottom: "1px solid var(--border-color, rgba(59,130,246,0.07))",
                    backdropFilter: "blur(16px)", flexShrink: 0
                }}>
                    <button
                        onClick={() => { setActiveChatId(null); setShowInfoDrawer(false); }}
                        style={{
                            width: 34, height: 34, borderRadius: "50%",
                            background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.12)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", outline: "none", flexShrink: 0
                        }}
                    >
                        <ArrowBackIosNewIcon style={{ color: "#2563eb", fontSize: 14 }} />
                    </button>

                    <div style={{ position: "relative", flexShrink: 0 }}>
                        <div style={{
                            width: 42, height: 42, borderRadius: "50%",
                            background: `linear-gradient(135deg,${activeChat.chatColor},${activeChat.chatColor}88)`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                            boxShadow: `0 4px 12px ${activeChat.chatColor}30`
                        }}>
                            {activeChat.chatInitials}
                        </div>
                        <div style={{
                            position: "absolute", bottom: 1, right: 1, width: 10, height: 10,
                            borderRadius: "50%", background: isSessionActive ? "#22c55e" : "#94a3b8", border: "2px solid var(--card-bg, #fff)"
                        }} />
                    </div>

                    <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <p style={{ color: "var(--text-main, #0f172a)", fontSize: 14, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                {activeChat.chatName}
                            </p>
                            {isSessionActive && <VerifiedIcon style={{ fontSize: 12, color: "#3b82f6" }} />}
                        </div>
                        <p style={{ color: isTyping ? "#3b82f6" : (isSessionActive ? "#22c55e" : "var(--text-muted, #94a3b8)"), fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: 0, marginTop: 1 }}>
                            {isTyping ? "typing..." : (isSessionActive ? "online" : "completed")}
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: 6 }}>
                        <button
                            onClick={() => setShowInfoDrawer(!showInfoDrawer)}
                            style={{
                                width: 34, height: 34, borderRadius: "50%",
                                background: showInfoDrawer ? "rgba(37,99,235,0.12)" : "rgba(59,130,246,0.06)",
                                border: "1px solid rgba(59,130,246,0.12)", display: "flex", alignItems: "center",
                                justifyContent: "center", cursor: "pointer", outline: "none"
                            }}
                            title="Session parameters"
                        >
                            <InfoOutlinedIcon style={{ color: "#2563eb", fontSize: 16 }} />
                        </button>
                    </div>
                </div>

                {/* Messages Area */}
                <div
                    className="no-scrollbar"
                    style={{
                        flex: 1, overflowY: "auto", padding: "12px 14px",
                        display: "flex", flexDirection: "column", gap: 8,
                        background: "linear-gradient(180deg, rgba(59,130,246,0.02) 0%, var(--body-bg, #f8fafc) 100%)"
                    }}
                >
                    {/* Loading Messages Indicator */}
                    {loadingMessages && (
                        <div style={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: 200, gap: 12 }}>
                            <div className="bounce-dot" style={{ width: 10, height: 10, borderRadius: "50%", background: "#3b82f6", animation: "bounceDots 1.4s infinite ease-in-out" }} />
                            <span style={{ fontSize: 11, color: "var(--text-muted, #94a3b8)", fontFamily: "'DM Sans',sans-serif" }}>Loading messages...</span>
                        </div>
                    )}

                    {/* Empty session starter topics */}
                    {!loadingMessages && messages.length === 0 && !isTyping && (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 40, gap: 12 }}>
                            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(59,130,246,0.08)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(59,130,246,0.12)", marginBottom: 4 }}>
                                <HelpOutlineIcon style={{ color: "#3b82f6", fontSize: 20 }} />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-main, #0f172a)", fontFamily: "'Syne',sans-serif", textAlign: "center" }}>No messages yet</span>
                            <p style={{ fontSize: 11, color: "var(--text-muted, #94a3b8)", fontFamily: "'DM Sans',sans-serif", margin: "0 0 10px 0", textAlign: "center", maxWidth: 220 }}>Select a starter topic below to chat with {activeChat.chatName}:</p>

                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 360 }}>
                                {topics.map((t, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => send(t.text)}
                                        style={{
                                            padding: "8px 12px", borderRadius: 16, background: "var(--card-bg, #fff)",
                                            border: "1px solid var(--border-color, rgba(59,130,246,0.12))", cursor: "pointer",
                                            display: "flex", alignItems: "center", gap: 6, fontSize: 11,
                                            fontFamily: "'DM Sans',sans-serif", color: "var(--text-main, #334155)",
                                            boxShadow: "0 2px 6px rgba(59,130,246,0.04)"
                                        }}
                                    >
                                        <span>{t.icon}</span>
                                        <span style={{ fontWeight: 500 }}>{t.text}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {!loadingMessages && messages.map((msg) => {
                        const isMe = msg.role === "user";
                        return (
                            <div key={msg.id} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 8 }}>
                                {!isMe && (
                                    <div style={{
                                        width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                                        background: `linear-gradient(135deg,${activeChat.chatColor},${activeChat.chatColor}88)`,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                        alignSelf: "flex-end"
                                    }}>
                                        {activeChat.chatInitials}
                                    </div>
                                )}
                                <div style={{ maxWidth: "72%" }}>
                                    <div style={{
                                        padding: "10px 14px", borderRadius: 18,
                                        ...(isMe ? {
                                            background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
                                            borderBottomRightRadius: 4,
                                            boxShadow: "0 4px 18px rgba(37,99,235,0.22)"
                                        } : {
                                            background: "var(--card-bg, #ffffff)",
                                            border: "1px solid var(--border-color, rgba(59,130,246,0.08))",
                                            boxShadow: "var(--card-shadow, 0 2px 10px rgba(59,130,246,0.04))",
                                            borderBottomLeftRadius: 4
                                        })
                                    }}>
                                        <p style={{
                                            color: isMe ? "#eff6ff" : "var(--text-main, #334155)",
                                            fontSize: 13, fontFamily: "'DM Sans',sans-serif",
                                            lineHeight: 1.55, margin: 0
                                        }}>{msg.message}</p>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 3, justifyContent: isMe ? "flex-end" : "flex-start" }}>
                                        <span style={{ color: "var(--text-muted, #94a3b8)", fontSize: 9, fontFamily: "'DM Sans',sans-serif" }}>
                                            {msg.isTemp ? "Sending..." : (msg.timeCreated || "")}
                                        </span>
                                        {isMe && (
                                            msg.isTemp
                                                ? <DoneIcon style={{ fontSize: 12, color: "#cbd5e1" }} />
                                                : <DoneAllIcon style={{ fontSize: 12, color: "#3b82f6" }} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Typing indicator */}
                    {!loadingMessages && isTyping && (
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                            <div style={{
                                width: 28, height: 28, borderRadius: "50%",
                                background: `linear-gradient(135deg,${activeChat.chatColor},${activeChat.chatColor}88)`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: "'Syne',sans-serif"
                            }}>{activeChat.chatInitials}</div>
                            <div style={{
                                padding: "12px 18px", borderRadius: 18, borderBottomLeftRadius: 4,
                                background: "var(--card-bg, #fff)", border: "1px solid var(--border-color, rgba(59,130,246,0.08))",
                                boxShadow: "var(--card-shadow, 0 2px 10px rgba(59,130,246,0.04))",
                                display: "flex", gap: 5, alignItems: "center"
                            }}>
                                {[0, 1, 2].map(i => (
                                    <div key={i} className="bounce-dot" style={{
                                        width: 6, height: 6, borderRadius: "50%", background: "var(--text-muted, #94a3b8)",
                                        animation: `bounceDots 1.4s infinite ease-in-out both`,
                                        animationDelay: `${i * 0.2}s`
                                    }} />
                                ))}
                            </div>
                        </div>
                    )}

                    <div ref={endRef} />
                </div>

                {/* Input Bar */}
                <div style={{
                    padding: "10px 14px", background: "var(--bar-bg, rgba(255,255,255,0.97))",
                    borderTop: "1px solid var(--border-color, rgba(59,130,246,0.07))", backdropFilter: "blur(16px)", flexShrink: 0
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <button
                            disabled={!isSessionActive}
                            onClick={() => send("Tell me a random tip for financial wellness")}
                            style={{
                                width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                                background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.12)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: isSessionActive ? "pointer" : "not-allowed", outline: "none", opacity: isSessionActive ? 1 : 0.5
                            }}
                            title="Helpful prompt"
                        >
                            <HelpOutlineIcon style={{ color: "#3b82f6", fontSize: 20 }} />
                        </button>

                        <div style={{
                            flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "0 12px",
                            height: 42, background: isSessionActive ? "var(--body-bg, #f1f5f9)" : "var(--border-color, #e2e8f0)", borderRadius: 21,
                            border: "1px solid var(--border-color, rgba(59,130,246,0.08))", opacity: isSessionActive ? 1 : 0.7,
                            position: "relative"
                        }}>
                            <input
                                ref={inputRef}
                                value={input}
                                disabled={!isSessionActive}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && send()}
                                placeholder={isSessionActive ? `Message ${activeChat.chatName}...` : "This chat session is completed"}
                                style={{
                                    flex: 1, background: "transparent", border: "none", outline: "none",
                                    color: "var(--text-main, #0f172a)", fontSize: 13, fontFamily: "'DM Sans',sans-serif",
                                    cursor: isSessionActive ? "text" : "not-allowed"
                                }}
                            />
                            <button
                                disabled={!isSessionActive}
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                style={{ background: "none", border: "none", cursor: isSessionActive ? "pointer" : "not-allowed", display: "flex", alignItems: "center", padding: 0 }}
                            >
                                <SentimentSatisfiedAltIcon style={{ color: showEmojiPicker ? "#2563eb" : "var(--text-muted, #64748b)", fontSize: 18 }} />
                            </button>

                            {showEmojiPicker && (
                                <div style={{
                                    position: "absolute",
                                    bottom: 50,
                                    right: 10,
                                    width: 280,
                                    height: 200,
                                    background: "var(--card-bg, rgba(255, 255, 255, 0.95))",
                                    backdropFilter: "blur(12px)",
                                    border: "1px solid var(--border-color, rgba(59, 130, 246, 0.15))",
                                    borderRadius: 16,
                                    boxShadow: "var(--card-shadow, 0 8px 32px rgba(0, 0, 0, 0.12))",
                                    padding: 12,
                                    zIndex: 1000,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8
                                }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--border-color, rgba(0,0,0,0.05))", paddingBottom: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted, #64748b)", fontFamily: "'Syne', sans-serif" }}>Choose Emoji</span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowEmojiPicker(false);
                                            }}
                                            style={{ border: "none", background: "none", color: "#ef4444", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                                        >
                                            Close
                                        </button>
                                    </div>
                                    <div className="no-scrollbar" style={{
                                        flex: 1,
                                        overflowY: "auto",
                                        display: "grid",
                                        gridTemplateColumns: "repeat(8, 1fr)",
                                        gap: 6,
                                        padding: 2
                                    }}>
                                        {EMOJIS.map((emoji, idx) => (
                                            <button
                                                key={idx}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setInput(prev => prev + emoji);
                                                    inputRef.current?.focus();
                                                }}
                                                style={{
                                                    border: "none",
                                                    background: "none",
                                                    fontSize: 18,
                                                    cursor: "pointer",
                                                    padding: 4,
                                                    borderRadius: 6,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    transition: "background 0.1s"
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = "rgba(59, 130, 246, 0.1)"}
                                                onMouseLeave={e => e.currentTarget.style.background = "none"}
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => send()}
                            disabled={!isSessionActive || !input.trim()}
                            style={{
                                width: 42, height: 42, borderRadius: "50%", flexShrink: 0,
                                background: (input.trim() && isSessionActive) ? "linear-gradient(135deg,#3b82f6,#2563eb)" : "rgba(59,130,246,0.06)",
                                border: (input.trim() && isSessionActive) ? "none" : "1px solid rgba(59,130,246,0.12)",
                                boxShadow: (input.trim() && isSessionActive) ? "0 4px 16px rgba(59,130,246,0.25)" : "none",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: (input.trim() && isSessionActive) ? "pointer" : "not-allowed", outline: "none"
                            }}
                        >
                            <SendIcon style={{ color: (input.trim() && isSessionActive) ? "#fff" : "#94a3b8", fontSize: 18 }} />
                        </button>
                    </div>
                </div>

                {/* CSS styles for bouncing dots */}
                <style>{`
                    @keyframes bounceDots {
                        0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
                        40% { transform: scale(1.0); opacity: 1.0; }
                    }
                    .no-scrollbar::-webkit-scrollbar {
                        display: none;
                    }
                    .chats-height-container {
                        height: calc(100vh - 128px) !important;
                        height: calc(100dvh - 128px) !important;
                    }
                `}</style>

                {/* Info Drawer overlay */}
                {showInfoDrawer && renderInfoDrawer()}
            </div>
        );
    };

    // ── Contact List View ─────────────────────────────────────────────────────
    const renderList = () => (
        <div className="chats-height-container" style={{ display: "flex", flexDirection: "column", flex: 1 }}>

            {/* Header */}
            <div style={{ padding: "14px 16px 0", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                        <h2 style={{ color: "var(--text-main, #0f172a)", fontSize: 22, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: 0, lineHeight: 1.2 }}>
                            Messages
                        </h2>
                        {totalUnread > 0 && (
                            <p style={{ color: "var(--text-muted, #64748b)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: 0, marginTop: 2 }}>
                                {totalUnread} unread message{totalUnread !== 1 ? "s" : ""}
                            </p>
                        )}
                    </div>

                    {/* Wallet / Withdraw shortcut */}
                    <button
                        onClick={() => navigate('/atm')}
                        title="Withdraw earnings"
                        style={{
                            width: 40, height: 40, borderRadius: "50%",
                            background: "var(--border-color, rgba(59,130,246,0.08))",
                            border: "1.5px solid var(--border-color, rgba(59,130,246,0.18))",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", outline: "none", marginRight: 8,
                            transition: "background 0.15s",
                        }}
                    >
                        <AccountBalanceWalletIcon style={{ color: "#2563eb", fontSize: 19 }} />
                    </button>

                    {/* Start new chat */}
                    <button
                        onClick={() => navigate('/deposit')}
                        title="Start new chat session"
                        style={{
                            width: 40, height: 40, borderRadius: "50%",
                            background: "linear-gradient(135deg,#3b82f6,#2563eb)",
                            border: "none", boxShadow: "0 4px 14px rgba(59,130,246,0.3)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", outline: "none"
                        }}>
                        <AddIcon style={{ color: "#fff", fontSize: 20 }} />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="chats-search-bar" style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "0 12px",
                    height: 40, borderRadius: 20,
                    border: "1px solid var(--border-color, rgba(59,130,246,0.08))", marginBottom: 14
                }}>
                    <SearchIcon style={{ color: "#94a3b8", fontSize: 17 }} />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search conversations..."
                        style={{
                            flex: 1, background: "transparent", border: "none", outline: "none",
                            color: "var(--text-main, #0f172a)", fontSize: 13, fontFamily: "'DM Sans',sans-serif"
                        }}
                    />
                </div>

                {/* Tab Pills */}
                <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                    {TABS.map(tab => {
                        const isActive = activeTab === tab;
                        return (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    padding: "5px 14px", borderRadius: 20, cursor: "pointer", outline: "none",
                                    fontSize: 12, fontFamily: "'DM Sans',sans-serif", fontWeight: isActive ? 600 : 400,
                                    background: isActive ? "linear-gradient(135deg,#3b82f6,#2563eb)" : "var(--border-color, rgba(59,130,246,0.05))",
                                    color: isActive ? "#fff" : "var(--text-muted, #64748b)",
                                    border: isActive ? "none" : "1px solid var(--border-color, rgba(59,130,246,0.08))",
                                    boxShadow: isActive ? "0 4px 12px rgba(59,130,246,0.2)" : "none",
                                    display: "flex", alignItems: "center", gap: 5
                                }}
                            >
                                {tab}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="no-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>

                {/* Active Now Strip */}
                {activeTab === "All" && !search && chats.filter(c => Number(c.active) === 1).length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <span style={{ color: "var(--text-muted, #64748b)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                                Active Now
                            </span>
                        </div>
                        {renderActiveStrip()}
                    </div>
                )}

                {/* Conversations */}
                {loadingChats ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 120 }}>
                        <div className="bounce-dot" style={{ width: 10, height: 10, borderRadius: "50%", background: "#3b82f6", animation: "bounceDots 1.4s infinite ease-in-out" }} />
                    </div>
                ) : filtered.length > 0 ? (
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4, marginTop: 8 }}>
                            <span style={{ color: "var(--text-muted, #94a3b8)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                                {activeTab} Sessions
                            </span>
                        </div>
                        {filtered.map(c => renderContactCard(c))}
                    </div>
                ) : (
                    renderEmptyState()
                )}
            </div>
        </div>
    );

    return (
        <SiteWide id="main" title={"Chats"}>
            <Section overlayText={""} loading={false} title={'Chats'} unpadded={true} noScroll={true}>
                {activeChatId ? renderChatWindow() : renderList()}
            </Section>
        </SiteWide>
    );
}

export default Chats;